import { NgTemplateOutlet } from '@angular/common';
import { Component, effect, Input, input, model, output, TemplateRef, viewChildren } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { HistoryChange, HistoryEvent, WindowSize } from '@domains';
import {
  CharityService,
  DonationsService,
  JourneysService,
  LeadsService,
  LocalStorageService,
  MarketsService,
  OrganizationsService,
  PartnerService,
  PhoneConfigurationService,
  ScheduleHistoryService,
  StoresService,
  TrucksService,
  UsersService,
  XmilesService,
} from '@rspl-api';
import { CardComponent, FxFlexDirective, FxLayoutAlignDirective, FxLayoutDirective, FxLayoutGapDirective } from '@rspl-ui';
import { Observable, Subscription, takeUntil } from 'rxjs';

import { Responsive, ResponsiveService } from '../responsive';
import { SearchableSelectComponent } from '../searchable-select';
import { HistoryService } from './history.service';
import { HistoryConfig } from './history-config';
import { HistoryEntity } from './history-entity';

@Component({
  selector: 'rspl-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
  imports: [
    CardComponent,
    FxLayoutDirective,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatButtonModule,
    NgTemplateOutlet,
    SearchableSelectComponent,
    MatIcon,
    FxLayoutAlignDirective,
    FxLayoutGapDirective,
    FxFlexDirective,
  ],
})
export class HistoryComponent extends Responsive {
  isChild = input<boolean>(false);
  expansionPanels = viewChildren<MatExpansionPanel>(MatExpansionPanel);
  initialValueLabel = input<string>('Initial Value');
  history: Array<HistoryEvent> = new Array<HistoryEvent>();
  item: any;
  smallWindows: WindowSize[] = [WindowSize.XS, WindowSize.SM];
  changes = model<HistoryChange[]>([]);
  listSelector$?: Subscription;
  itemSelector$?: Subscription;

  showFilter = model<boolean>(true);
  filteredChanges: HistoryChange[] = this.changes();
  selectedFilter = model<string>('');
  onFilterChange = output<string>();

  showSort = model<boolean>(true);
  sort = model<'desc' | 'asc'>();
  sortedChanges: HistoryChange[] = this.filteredChanges;
  onSortChange = output<'desc' | 'asc'>();

  showExpandToggle = model<boolean>(true);
  hasExpanded = model<boolean>(false);
  onExpandToggle = output<boolean>();

  initialEvent: string;
  initialValue: { [key: string]: string };

  filterOptions = model<string[]>();
  onSetFilterOptions = output<string[]>();

  customFilterTemplate = input<TemplateRef<any>>();

  #config!: HistoryConfig;
  @Input() set config(config: HistoryConfig) {
    this.#config = config;
    if (config.itemIds) {
      this.listSelector$?.unsubscribe();
      this.itemSelector$?.unsubscribe();
      this.item = undefined;
      this.listSelector$ = this.service
        ?.history(config.itemIds)
        .pipe(takeUntil(this.destroy$))
        .subscribe((history) => {
          this.history = history.filter((h) => Object.keys(h.changes).length > 0);
          this.historyService
            .mapChanges(this.history, this.config.users, this.config.replaceFields, this.config.fieldTypes, this.config.ignoreFields)
            .subscribe((res) => {
              this.changes.set(res);
              this.filteredChanges = res;
              if (this.config.initialEvent) {
                this.initialEvent = this.historyService.getEvent(this.config.initialEvent);
                this.initialValue = this.changes()
                  .find((h) => h.event === this.initialEvent)
                  ?.fields?.reduce((obj, item) => Object.assign(obj, { [item.name]: item.newValue }), {});
              }
              let fields = [];
              this.changes().forEach((change) => {
                fields = [...fields, ...change.fields.map((field) => field.name)];
              });

              this.filterOptions.set([...new Set(fields)].sort());
              this.onSetFilterOptions.emit(this.filterOptions());
            });
        });
      if (this.config.shouldGetItem)
        this.itemSelector$ = this.service
          ?.find?.(config.itemIds[0])
          .pipe(takeUntil(this.destroy$))
          .subscribe((item) => {
            this.item = item;
          });
    }
  }

  changeSort() {
    this.sort.set(this.sort() === 'asc' ? 'desc' : 'asc');
    this.onSortChange.emit(this.sort());
    this.localStorage.setItem('RSPL_HISTORY_SORT', this.sort());
  }

  filterChange(event: string): void {
    this.selectedFilter.set(event);
    this.onFilterChange.emit(this.selectedFilter());
    setTimeout(() => {
      this.toggleAllPanels(event?.length > 0);
    });
  }

  get config(): HistoryConfig {
    return this.#config;
  }

  get service():
    | {
        find?(id: string): Observable<any>;
        history(ids: string[]): Observable<Array<HistoryEvent>>;
      }
    | undefined {
    switch (this.config.entity) {
      case HistoryEntity.CHARITY:
        return this.charityService;
      case HistoryEntity.DONATION:
        return this.donationsService;
      case HistoryEntity.LEAD:
        return this.leadsService;
      case HistoryEntity.JOURNEY:
        return this.journeyService;
      case HistoryEntity.MARKET:
        return this.marketService;
      case HistoryEntity.PARTNER:
        return this.partnerService;
      case HistoryEntity.SCHEDULE:
        return this.scheduleHistoryService;
      case HistoryEntity.STORE:
        return this.storeService;
      case HistoryEntity.TRUCK:
        return this.truckService;
      case HistoryEntity.USER:
        return this.userService;
      case HistoryEntity.XMILE:
        return this.xmileService;
      case HistoryEntity.ORGANIZATION:
        return this.organizationsService;
      case HistoryEntity.PHONE_CONFIGURATION:
        return this.phoneConfigurationService;
      default:
        return undefined;
    }
  }

  constructor(
    public override responsiveService: ResponsiveService,
    private localStorage: LocalStorageService,
    private historyService: HistoryService,
    private donationsService: DonationsService,
    private leadsService: LeadsService,
    private charityService: CharityService,
    private partnerService: PartnerService,
    private storeService: StoresService,
    private truckService: TrucksService,
    private scheduleHistoryService: ScheduleHistoryService,
    private xmileService: XmilesService,
    private marketService: MarketsService,
    private userService: UsersService,
    private journeyService: JourneysService,
    private organizationsService: OrganizationsService,
    private phoneConfigurationService: PhoneConfigurationService,
  ) {
    super(responsiveService);

    effect(() => {
      if (!this.selectedFilter()) {
        this.filteredChanges = this.changes();
        this.toggleAllPanels(false);
      } else {
        this.filteredChanges = this.changes()
          .filter((change) => {
            return change.fields.find((field) => {
              return field.name === this.selectedFilter();
            });
          })
          .map((change) => ({
            ...change,
            fields: change.fields.filter((field) => {
              return field.name === this.selectedFilter();
            }),
          }));
      }
      if (this.sort() === 'desc') {
        this.sortedChanges = [...this.filteredChanges];
      } else {
        this.sortedChanges = [...this.filteredChanges].reverse();
      }
    });
    this.sort.set((this.localStorage.getItem('RSPL_HISTORY_SORT') as 'asc' | 'desc') || 'asc');
    this.onSortChange.emit(this.sort());
  }

  toggleAllPanels(expand: boolean) {
    if (!expand) {
      this.expansionPanels()?.forEach((panel) => panel.close());
      this.onExpandToggle.emit(false);
    } else {
      this.expansionPanels()?.forEach((panel) => panel.open());
      this.onExpandToggle.emit(true);
    }
  }

  hasExpandedPanel() {
    return this.expansionPanels()?.find((x) => x.expanded) || this.hasExpanded();
  }
}
